import { PageTitle } from 'components/common/PageTitle/PageTitle';
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks';
import { useCallback, useState, useEffect } from 'react';
import { CheckerEmployee, Device } from 'types/devices';
import { useNavigate, useParams } from 'react-router';
import * as Sentry from '@sentry/react';
import { Button, Col, Result, Row, Skeleton } from 'antd';
import { doList } from 'store/slices/devicesSlice';
import { DeviceDetail } from 'components/devices/DeviceDetail';
import { globalPaths } from 'constants/routePaths';
import { SliceStatus } from 'constants/enums/slices';
import { getDeviceEmployees } from 'api/devices.api';

export const DeviceDetailPage = () => {
  const params = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { devices, status: deviceSliceStatus } = useAppSelector(
    (state) => state.device
  );
  const [device, setDevice] = useState<Device>();
  const [deviceEmployees, setDeviceEmployees] = useState<CheckerEmployee[]>([]);
  const [loadingDeviceEmployees, setLoadingDeviceEmployees] = useState(false);
  const [deviceEmployeesError, setDeviceEmployeesError] = useState<string>();

  useEffect(() => {
    if (deviceSliceStatus === SliceStatus.Empty) {
      dispatch(doList());
    } else if (deviceSliceStatus === SliceStatus.Fulfilled) {
      const currentDevice = devices
        .filter((c) => c && c.serialNumber === params.sn)
        .at(0);
      if (currentDevice) {
        setDevice(currentDevice);
      } else {
        Sentry.captureMessage('Not able to find device');
      }
    } else {
      if (deviceSliceStatus !== SliceStatus.Updating) {
        Sentry.captureMessage('Not able to find a checksData');
      }
    }
  }, [dispatch, devices, deviceSliceStatus, params.sn]);

  const loadDeviceEmployees = useCallback(async () => {
    if (!device?.serialNumber) return;
    setLoadingDeviceEmployees(true);
    setDeviceEmployeesError(undefined);
    try {
      const response = await getDeviceEmployees(device.serialNumber);
      setDeviceEmployees(response.data);
    } catch (error) {
      Sentry.captureException(error);
      setDeviceEmployeesError(
        'No se pudo actualizar la lista de empleados. Pulsa «Actualizar lista» para volver a intentar.'
      );
    } finally {
      setLoadingDeviceEmployees(false);
    }
  }, [device?.serialNumber]);

  useEffect(() => {
    setDeviceEmployees([]);
    void loadDeviceEmployees();
  }, [loadDeviceEmployees]);

  return (
    <>
      <PageTitle>Dispositivos</PageTitle>
      {deviceSliceStatus === SliceStatus.Updating && !devices && (
        <Row>
          <Col xs={24}>
            <Skeleton active />
          </Col>
        </Row>
      )}

      {deviceSliceStatus === SliceStatus.Fulfilled && device && (
        <DeviceDetail
          device={device}
          employees={deviceEmployees}
          employeesLoading={loadingDeviceEmployees}
          employeesError={deviceEmployeesError}
          onEmployeesRefresh={loadDeviceEmployees}
        />
      )}
      {!devices && deviceSliceStatus === SliceStatus.Fulfilled && (
        <Result
          status="error"
          title="Hubo un error al cargar los datos"
          subTitle="Por favor vuelve a intentarlo más tarde."
          extra={[
            <Button
              type="primary"
              key="back"
              onClick={() => navigate(globalPaths.basePath)}
            >
              Regresar
            </Button>,
            <Button key="reload" onClick={() => window.location.reload()}>
              Reintentar
            </Button>,
          ]}
        />
      )}
    </>
  );
};
